'use client';

import * as React from 'react';
import { Switch as SwitchPrimitive } from 'radix-ui';

import { cn } from '@/lib/utils';

type SwitchSize = 'sm' | 'default';

const TRACK_SIZE: Readonly<Record<SwitchSize, string>> = {
    default: 'h-5.5 w-10',
    sm: 'h-4.5 w-8',
};

// How far the thumb travels depends on its size, so the two are set together.
const THUMB_SIZE: Readonly<Record<SwitchSize, string>> = {
    default: 'size-4.5 data-checked:translate-x-4.5 data-unchecked:translate-x-0',
    sm: 'size-3.5 data-checked:translate-x-[15px] data-unchecked:translate-x-px rtl:data-checked:-translate-x-[15px] rtl:data-unchecked:-translate-x-px',
};

function Switch({
    className,
    size = 'default',
    ...props
}: React.ComponentProps<typeof SwitchPrimitive.Root> & {
    readonly size?: SwitchSize;
}) {
    return (
        <SwitchPrimitive.Root
            data-slot="switch"
            data-size={size}
            className={cn(
                'peer group/switch relative inline-flex shrink-0 items-center rounded-full border border-transparent transition-all duration-150 outline-none cursor-pointer after:absolute after:-inset-x-3 after:-inset-y-2 focus-visible:ring-3 focus-visible:ring-primary-200 data-checked:bg-primary-500 data-unchecked:bg-neutral-300 dark:data-unchecked:bg-border-strong data-disabled:cursor-not-allowed data-disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40',
                TRACK_SIZE[size],
                className,
            )}
            {...props}
        >
            <SwitchPrimitive.Thumb
                data-slot="switch-thumb"
                className={cn(
                    'pointer-events-none block rounded-full bg-white shadow-sm ring-0 transition-transform duration-150 dark:bg-neutral-200',
                    THUMB_SIZE[size],
                )}
            />
        </SwitchPrimitive.Root>
    );
}

export { Switch };
